import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { FaCog, FaMapMarkedAlt, FaBrain, FaFileAlt, FaTable } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { useRoles } from '../hooks/useRoles';
import { LuListFilter } from 'react-icons/lu';
import CommunityFilterPanel from './CommunityFilterPanel';
import OutboxStatus from './OutboxStatus';
import hazardService from '../services/hazardService';
import {
  CommunityFilters,
  CommunityFilterFacets,
  defaultCommunityFilters,
  countActiveFilters,
} from '../types/communityFilters';
import { DashboardFilterContext } from '../contexts/DashboardFilterContext';
import { useAuth } from '../hooks/useAuth';
import notificationService from '../services/notificationService';
import eventEmitter, { EVENTS } from '../services/eventService';
import userProfileService, { UserProfile as UserProfileType } from '../services/userProfileService';
import eventStreamService from '../services/eventStreamService';
import API_BASE_URL, { IS_B2B, HOME_ROUTE } from '../config';
import { getAuthHeaders } from '../utils/authHeaders';
import { useTutorial } from '../tutorial/TutorialContext';
import TutorialOverlay from '../tutorial/TutorialOverlay';
import { useTenantEntitlements } from '../hooks/useTenantEntitlements';

/** Route chunks load on demand to shrink the main bundle (see frontend perf plan). */
const Community = lazy(() => import('../pages/Community'));
const Settings = lazy(() => import('../pages/Settings'));
const AnalyticsLayout = lazy(() => import('../pages/analytics/AnalyticsLayout'));
const BasicAnalytics = lazy(() => import('../pages/analytics/BasicAnalytics'));
const GeoAnalytics = lazy(() => import('../pages/analytics/GeoAnalytics'));
const AiAnalytics = lazy(() => import('../pages/analytics/AiAnalytics'));
const ReportsAnalytics = lazy(() => import('../pages/analytics/ReportsAnalytics'));
const HazardsTable = lazy(() => import('../pages/analytics/HazardsTable'));
const AdminPanel = lazy(() => import('../pages/AdminPanel'));
const DeletedHazards = lazy(() => import('../pages/DeletedHazards'));
const OrphanedMedia = lazy(() => import('../pages/OrphanedMedia'));
const IntelexIntegration = lazy(() => import('../pages/IntelexIntegration'));
const UserManagement = lazy(() => import('../pages/UserManagement'));
const UserProfile = lazy(() => import('../pages/UserProfile'));
const SocketTest = lazy(() => import('../pages/SocketTest'));
const WebSocketConsistencyTest = lazy(() => import('../pages/WebSocketConsistencyTest'));
const OfflineTest = lazy(() => import('../pages/OfflineTest'));
const LexiconDebug = lazy(() => import('../pages/LexiconDebug'));
const ViewHazard = lazy(() => import('../pages/ViewHazard'));

const routeLoadingFallback = (
  <div
    data-testid="loading"
    className="flex justify-center items-center min-h-[40vh] text-gray-500 dark:text-gray-300 text-sm"
  >
    Loading…
  </div>
);

/** Holds Geo/AI/Reports/Hazards until the entitlement check finishes, then redirects when denied. */
const AdvancedAnalyticsRoute: React.FC<{
  allow: boolean;
  loading: boolean;
  children: React.ReactElement;
}> = ({ allow, loading, children }) => {
  if (loading) return routeLoadingFallback;
  if (!allow) return <Navigate to="/app/communitydashboard" replace />;
  return children;
};

interface TabItem {
  path: string;
  label: string;
  icon?: IconType; // Make icon optional since Community won't use it
  component: React.ComponentType;
}

// react-icons components are typed loosely; this thin wrapper keeps JSX/TS happy.
const NavIcon: React.FC<{ component: IconType; className?: string }> = ({ component: Cmp, className }) => {
  const C = Cmp as unknown as React.ComponentType<{ className?: string }>;
  return <C className={className} />;
};

interface B2bNavItem {
  to: string; // relative to /app
  label: string;
  icon: IconType;
  gradient: string; // tailwind gradient for the active accent
}

// B2B shell navigation rail — the analytics sections (Geo/AI/Reports) promoted to
// the app chrome. Kept in sync with AnalyticsLayout's TABS (Stats is community-only).
const B2B_NAV_ITEMS: B2bNavItem[] = [
  { to: 'communitydashboard/geo', label: 'Geo', icon: FaMapMarkedAlt, gradient: 'from-violet-500 to-indigo-500' },
  { to: 'communitydashboard/ai', label: 'AI', icon: FaBrain, gradient: 'from-cyan-500 to-blue-500' },
  { to: 'communitydashboard/reports', label: 'Reports', icon: FaFileAlt, gradient: 'from-emerald-500 to-teal-500' },
  { to: 'communitydashboard/hazards', label: 'Hazards', icon: FaTable, gradient: 'from-fuchsia-500 to-pink-500' },
];

const MainLayout: React.FC = () => {
  const location = useLocation();
  const { hasAdvancedAnalytics, isLoading: entitlementsLoading } = useTenantEntitlements();
  const showAdvancedAnalytics = hasAdvancedAnalytics && !entitlementsLoading;
  const isOnCommunityPage = location.pathname === '/app/community';
  // In B2B the analytics dashboard (Geo/AI/Reports) reuses the community filter
  // drawer as its single, global filter surface — shown on any dashboard route.
  const isOnDashboard = location.pathname.startsWith('/app/communitydashboard');
  // The Hazards table owns its own standalone per-column filters, so the global
  // filter drawer is hidden there to avoid two competing filter surfaces.
  const isOnHazardsTable = location.pathname.startsWith('/app/communitydashboard/hazards');
  const showFilterButton =
    isOnCommunityPage ||
    (IS_B2B && showAdvancedAnalytics && isOnDashboard && !isOnHazardsTable);

  // Measure the fixed navbar's real rendered height (border-box, so it includes
  // the 1px bottom border) and use it verbatim as <main>'s top padding. This keeps
  // page content cleared by EXACTLY the navbar height on every device — so the
  // Community feed's own top padding renders as the true visible gap between the
  // navbar and the first card (matching the inter-card gap). It self-corrects if
  // the navbar height ever changes (responsive breakpoints, wrapping, zoom).
  const navRef = useRef<HTMLElement>(null);
  const [navHeight, setNavHeight] = useState<number>(64); // h-16 fallback avoids first-paint overlap
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      const next = nav.getBoundingClientRect().height;
      setNavHeight((prev) => (prev === next ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    });
    observer.observe(nav);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  // Community filter state (rich multi-filter drawer). The funnel is only shown
  // on the community page, so lifting this here matches the existing pattern.
  const [communityFilters, setCommunityFilters] = useState<CommunityFilters>(defaultCommunityFilters);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterFacets, setFilterFacets] = useState<CommunityFilterFacets>({ users: [], regions: [] });
  const [facetsLoading, setFacetsLoading] = useState(false);

  // Open the drawer: ensure the whole dataset is synced locally (once), then
  // build the author/region pick-lists from it.
  const openFilterPanel = async () => {
    setFilterPanelOpen(true);
    setFacetsLoading(true);
    try {
      if (navigator.onLine) {
        const all = await hazardService.fetchAllCommunityMetadata();
        const facets = await hazardService.getCommunityFilterFacets(all);
        setFilterFacets(facets);
      } else {
        const facets = await hazardService.getCommunityFilterFacets();
        setFilterFacets(facets);
      }
    } catch (error) {
      console.error('Error preparing community filters:', error);
    } finally {
      setFacetsLoading(false);
    }
  };

  const activeFilterCount = countActiveFilters(communityFilters);

  const tabs: TabItem[] = [
    { path: 'community', label: 'Community', component: Community }, // Remove icon property
    { path: 'settings', label: 'Settings', icon: FaCog, component: Settings },
  ];

  // Use server-side navigation configuration instead of client-side role checking
  const { 
    fetchNavigationConfig
  } = useRoles();
  const { isAuthenticated, userInfo } = useAuth();

  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated || !userInfo) {
        setUserProfile(null);
        return;
      }

      try {
        setProfileLoading(true);
        const profile = await userProfileService.getUserProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error('Error loading user profile:', error);
        setUserProfile(null);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [isAuthenticated, userInfo]);

  // Reflect profile edits (display name / avatar) in the nav immediately.
  useEffect(() => {
    const handleProfileUpdated = (data: { displayName?: string; username?: string; profileImageUrl?: string }) => {
      setUserProfile(prev =>
        prev
          ? {
              ...prev,
              displayName: data.displayName ?? data.username ?? prev.displayName,
              profileImageUrl: data.profileImageUrl ?? prev.profileImageUrl,
            }
          : prev
      );
    };

    eventEmitter.on(EVENTS.PROFILE_UPDATED, handleProfileUpdated);
    return () => {
      eventEmitter.off(EVENTS.PROFILE_UPDATED, handleProfileUpdated);
    };
  }, []);

  // Badge count: poll only when realtime is unavailable (WebSocket-primary).
  // B2B hides the notifications list, so skip the badge entirely there.
  useEffect(() => {
    if (!isAuthenticated || IS_B2B) {
      setUnreadCount(0);
      return;
    }

    let interval: ReturnType<typeof setInterval> | null = null;

    const loadNotificationCount = async () => {
      try {
        const count = await notificationService.fetchUnreadCount();
        setUnreadCount(count);
      } catch (e) {
        // silent fail
      }
    };

    const startPolling = () => {
      if (interval !== null) return;
      interval = setInterval(() => {
        void loadNotificationCount();
      }, 60000);
    };

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    void loadNotificationCount();

    const onRealtime = (connected: boolean) => {
      if (connected) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    eventStreamService.addConnectionListener(onRealtime);
    onRealtime(eventStreamService.isConnectedToStream());

    return () => {
      stopPolling();
      eventStreamService.removeConnectionListener(onRealtime);
    };
  }, [isAuthenticated]);

  // Realtime notifications via Web PubSub per-user hub - update count only
  useEffect(() => {
    if (!isAuthenticated || IS_B2B) return;
    const onNotif = () => {
      setUnreadCount(prev => prev + 1);
    };
    const onCleared = async () => {
      // Refresh the count when notifications are cleared
      try {
        const count = await notificationService.fetchUnreadCount();
        setUnreadCount(count);
      } catch (e) {
        // If fetch fails, just set to 0 since they were cleared
        setUnreadCount(0);
      }
    };
    const onMarkedRead = async () => {
      // Refresh the count when a notification is marked as read
      try {
        const count = await notificationService.fetchUnreadCount();
        setUnreadCount(count);
      } catch (e) {
        // Silent fail, count will update on next poll
      }
    };
    eventEmitter.on(EVENTS.NOTIFICATION_RECEIVED, onNotif);
    eventEmitter.on(EVENTS.NOTIFICATIONS_CLEARED, onCleared);
    eventEmitter.on(EVENTS.NOTIFICATION_MARKED_READ, onMarkedRead);
    return () => {
      eventEmitter.off(EVENTS.NOTIFICATION_RECEIVED, onNotif);
      eventEmitter.off(EVENTS.NOTIFICATIONS_CLEARED, onCleared);
      eventEmitter.off(EVENTS.NOTIFICATION_MARKED_READ, onMarkedRead);
    };
  }, [isAuthenticated]);

  // Lazy fetch navigation configuration only when needed
  // This prevents blocking page loads for pages that don't need navigation items
  useEffect(() => {
    if (isAuthenticated) {
      // Fetch navigation config in background without blocking page load
      // Use setTimeout to defer this until after initial page render
      const timer = setTimeout(() => {
        fetchNavigationConfig().catch(error => {
          console.warn('Background navigation config fetch failed:', error);
        });
      }, 100); // Small delay to ensure page loads first
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, fetchNavigationConfig]);

  useEffect(() => {
    const healthCheck = async () => {
      try {
        const headers = getAuthHeaders();
        await fetch(`${API_BASE_URL}/api/db-health-check`, { headers });
      } catch (error) {
        console.error('DB health check failed:', error);
      }
    };

    healthCheck();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        healthCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-start the guided tour once, on a user's first visit (per-device).
  // The tour targets community-feed elements, so it never auto-runs in the B2B dashboard.
  const { start: startTour, hasSeen: hasSeenTour, active: tourActive } = useTutorial();
  useEffect(() => {
    if (IS_B2B || hasSeenTour || tourActive) return;
    const t = setTimeout(() => startTour(), 700);
    return () => clearTimeout(t);
  }, [hasSeenTour, tourActive, startTour]);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-deck-ink">
      {/* Fixed navbar */}
      <nav ref={navRef} className="fixed top-0 left-0 right-0 bg-white dark:bg-deck-panel shadow dark:shadow-none border-b border-gray-200 dark:border-white/10 z-10">
        <div className="w-full h-16 flex items-center">
          {/* Left side - Filter Button (community feed, or the B2B dashboard) */}
          <div className="flex-1 flex justify-start items-center px-4 space-x-2">
            {showFilterButton && (
              <button
                type="button"
                onClick={openFilterPanel}
                className={`relative flex items-center justify-center border-b-2 text-sm font-medium h-16 px-3 transition-colors ${
                  activeFilterCount > 0 || filterPanelOpen
                    ? 'border-[#FF3EB5] text-[#FF3EB5]'
                    : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-pink-400'
                }`}
                title="Filter hazards"
                aria-label="Open filter options"
                data-tour="community-filter"
              >
                <LuListFilter className="w-6 h-7" />
                {activeFilterCount > 0 && (
                  <span className="absolute top-2 right-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold leading-none text-white bg-[#FF3EB5] rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
          </div>
          
          {/* Center - Home Tab with SVG Icon (Community feed, or Analytics in B2B mode) */}
          <div className="flex-1 flex justify-center">
            <NavLink
              to={HOME_ROUTE}
              className={({ isActive }) =>
                `flex items-center justify-center border-b-2 text-sm font-medium h-16 px-6 ${
                  isActive
                    ? 'border-pink-500 dark:border-pink-300 text-pink-600 dark:text-pink-200'
                    : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-pink-400'
                }`}
              title={IS_B2B ? 'Analytics' : 'Community'}
              aria-label={IS_B2B ? 'Analytics' : 'Community'}
            >
              {({ isActive }) => (
                <img 
                  src="/favicon/favicon.svg" 
                  alt={IS_B2B ? 'Analytics' : 'Community'} 
                  className="w-12 h-12"
                  style={{
                    filter: isActive 
                      ? 'brightness(0) saturate(100%) invert(45%) sepia(89%) saturate(2383%) hue-rotate(299deg) brightness(102%) contrast(101%)' // Pink for active state
                      : 'brightness(0) saturate(100%) invert(41%) sepia(8%) saturate(394%) hue-rotate(202deg) brightness(98%) contrast(95%)' // Gray for inactive state
                  }}
                />
              )}
            </NavLink>
          </div>
          
          {/* Right side - Navigation Tabs */}
          <div className="flex-1 flex justify-end items-center px-4 space-x-2">
            {/* Profile Icon Button (Settings) */}
            <div className="relative">
              <NavLink
                to="/app/settings"
                className={({ isActive }) =>
                  `relative flex items-center justify-center border-b-2 text-sm font-medium h-16 px-6 ${
                    isActive
                      ? 'border-[#FF3EB5] text-[#FF3EB5]'
                      : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-pink-400'
                  }`}
                title="Settings"
                aria-label="Settings"
              >
                {profileLoading ? (
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse" />
                ) : userProfile?.profileImageUrl ? (
                  <img
                    src={userProfile.profileImageUrl}
                    alt={userProfile.displayName || userProfile.username}
                    className="w-8 h-8 rounded-full object-cover border-2 border-current"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#FF3EB5] flex items-center justify-center text-white font-semibold text-sm">
                    {(userProfile?.displayName || userProfile?.username || userInfo?.userDetails || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[1.25rem]">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </NavLink>
            </div>
          </div>
        </div>
      </nav>

      {/* Content region: the fixed navbar is cleared by paddingTop. In B2B the app gets a
          full-height left rail (Geo/AI/Reports); paddingTop matches the measured navbar
          height so page content clears it exactly on every device. */}
      <div className="flex" style={{ paddingTop: navHeight }}>
        {IS_B2B && showAdvancedAnalytics && (
          <aside
            className="hidden sm:flex sm:flex-col shrink-0 w-24 gap-2 px-2 py-4 bg-white dark:bg-deck-panel border-r border-gray-200 dark:border-white/10 sticky self-start overflow-y-auto"
            style={{ top: navHeight, height: `calc(100vh - ${navHeight}px)` }}
            aria-label="Dashboard sections"
          >
            {B2B_NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={`/app/${item.to}`}
                title={item.label}
                className={({ isActive }) =>
                  [
                    'group relative flex flex-col items-center gap-1.5 rounded-2xl px-2 py-3 text-center transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-pink-400',
                    isActive ? 'shadow-lg' : 'hover:bg-gray-100 dark:hover:bg-white/5',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      aria-hidden
                      className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${item.gradient} transition-opacity ${
                        isActive ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <span
                      className={`relative h-10 w-10 rounded-xl flex items-center justify-center ${
                        isActive ? 'bg-white/20 text-white' : 'bg-gradient-to-br ' + item.gradient + ' text-white'
                      }`}
                    >
                      <NavIcon component={item.icon} className="h-5 w-5" />
                    </span>
                    <span
                      className={`relative text-xs font-semibold ${
                        isActive ? 'text-white' : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </aside>
        )}

        <main className="w-full min-w-0 pb-6 px-4 sm:px-6 lg:px-8 dark:text-white">
          {IS_B2B && showAdvancedAnalytics && (
            <nav
              aria-label="Dashboard sections"
              className="sm:hidden -mx-4 mb-2 px-4 pt-4 flex gap-2 overflow-x-auto"
            >
              {B2B_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={`/app/${item.to}`}
                  className={({ isActive }) =>
                    [
                      'relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all',
                      isActive ? 'shadow' : 'bg-white dark:bg-white/5',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        aria-hidden
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${item.gradient} transition-opacity ${
                          isActive ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <NavIcon
                        component={item.icon}
                        className={`relative h-4 w-4 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-300'}`}
                      />
                      <span className={`relative ${isActive ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          )}
          {/* In B2B the analytics pages read their filters from the global drawer
              via this provider; it is null in the community app so those pages
              keep their own local date-range control unchanged. */}
          <DashboardFilterContext.Provider value={IS_B2B ? communityFilters : null}>
          <Suspense fallback={routeLoadingFallback}>
          <OutboxStatus />
          <Routes>
            {!IS_B2B && (
              <Route 
                path="community" 
                element={
                  <Community 
                    communityFilters={communityFilters}
                  />
                } 
              />
            )}
            {!IS_B2B && <Route path="hazard/:hazardId" element={<ViewHazard />} />}
            {tabs.slice(1).map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
            <Route path="communitydashboard" element={<AnalyticsLayout />}>
              {IS_B2B && showAdvancedAnalytics ? (
                <Route index element={<Navigate to="reports" replace />} />
              ) : (
                <Route index element={<BasicAnalytics />} />
              )}
              <Route
                path="geo"
                element={
                  <AdvancedAnalyticsRoute allow={showAdvancedAnalytics} loading={entitlementsLoading}>
                    <GeoAnalytics />
                  </AdvancedAnalyticsRoute>
                }
              />
              <Route
                path="ai"
                element={
                  <AdvancedAnalyticsRoute allow={showAdvancedAnalytics} loading={entitlementsLoading}>
                    <AiAnalytics />
                  </AdvancedAnalyticsRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <AdvancedAnalyticsRoute allow={showAdvancedAnalytics} loading={entitlementsLoading}>
                    <ReportsAnalytics />
                  </AdvancedAnalyticsRoute>
                }
              />
              {IS_B2B && (
                <Route
                  path="hazards"
                  element={
                    <AdvancedAnalyticsRoute allow={showAdvancedAnalytics} loading={entitlementsLoading}>
                      <HazardsTable />
                    </AdvancedAnalyticsRoute>
                  }
                />
              )}
            </Route>
            <Route path="profile" element={<UserProfile />} />
            {/* Admin routes are enabled in B2B too; the pages are role-gated. */}
            <Route path="admin" element={<AdminPanel />} />
            <Route path="admin/deleted-hazards" element={<DeletedHazards />} />
            <Route path="admin/orphaned-media" element={<OrphanedMedia />} />
            {!IS_B2B && <Route path="admin/intelex" element={<IntelexIntegration />} />}
            <Route path="usermanagement" element={<UserManagement />} />
            {!IS_B2B && <Route path="socket-test" element={<SocketTest />} />}
            {!IS_B2B && <Route path="websocket-consistency-test" element={<WebSocketConsistencyTest />} />}
            {!IS_B2B && <Route path="offline-test" element={<OfflineTest />} />}
            {!IS_B2B && <Route path="lexicon-debug" element={<LexiconDebug />} />}
            <Route path="*" element={<Navigate to={IS_B2B ? 'communitydashboard' : 'community'} replace />} />
          </Routes>
          </Suspense>
          </DashboardFilterContext.Provider>
        </main>
      </div>

      {/* Community filter drawer — the community feed, or the B2B dashboard's
          single global filter surface. */}
      {showFilterButton && (
        <CommunityFilterPanel
          open={filterPanelOpen}
          filters={communityFilters}
          facets={filterFacets}
          facetsLoading={facetsLoading}
          onChange={setCommunityFilters}
          onClose={() => setFilterPanelOpen(false)}
        />
      )}

      {/* Guided product tour overlay */}
      <TutorialOverlay />
    </div>
  );
};

export default MainLayout;